import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FrameworkAction, FrameworkActionList, FrameworkClass } from '../framework/framework.d';
import { FrameworkClassType } from '../framework/framework-types';
import { projectSlice } from './ProjectSlice';
import type { InputBinding } from '../utils/inputBindings';
import type { StartCallConfig } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';

export interface ActionItem {
  parentName: string;
  parentClass?: FrameworkClass;
  mechanismClass?: FrameworkClass;
  mechanismName?: string;
  action: FrameworkAction;
}

/** Group label for action pickers: mechanism property name on a parent, else the top-level instance variable. */
export function actionMechanismGroupLabel(item: Pick<ActionItem, 'parentName' | 'mechanismName' | 'mechanismClass' | 'parentClass'>): string {
  if (item.mechanismClass?.type === FrameworkClassType.Utility || item.parentClass?.type === FrameworkClassType.Utility) {
    return 'utility';
  }
  const name = (item.mechanismName || item.parentName || '').trim();
  return name || 'Mechanism';
}
export interface CustomActionItem {
  name: string;
  lineNumber?: number;
}
interface EditorDerivedState {
  actionList: ActionItem[];
  axisActionList: ActionItem[];
  inputBindingList: InputBinding[];
  customActionList: CustomActionItem[];
  frameworkActionLists: FrameworkActionList[];
  startCallConfig: StartCallConfig | null;
}

const initialState: EditorDerivedState = {
  actionList: [],
  axisActionList: [],
  inputBindingList: [],
  customActionList: [],
  frameworkActionLists: [],
  startCallConfig: null,
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setActionList(state, action: PayloadAction<ActionItem[]>) {
      state.actionList = action.payload;
    },
    setAxisActionList(state, action: PayloadAction<ActionItem[]>) {
      state.axisActionList = action.payload;
    },
    setInputBindingList(state, action: PayloadAction<InputBinding[]>) {
      state.inputBindingList = action.payload;
    },
    setCustomActionList(state, action: PayloadAction<CustomActionItem[]>) {
      state.customActionList = action.payload;
    },
    setFrameworkActionLists(state, action: PayloadAction<FrameworkActionList[]>) {
      state.frameworkActionLists = action.payload;
    },
    setStartCallConfig(state, action: PayloadAction<StartCallConfig | null>) {
      state.startCallConfig = action.payload;
    },
  },
  extraReducers: (builder) => {
    const resetDerivedState = (state: EditorDerivedState) => {
      state.actionList = [];
      state.axisActionList = [];
      state.inputBindingList = [];
      state.customActionList = [];
      state.startCallConfig = null;
    };

    // Clear editor-derived state when switching projects to avoid stale UI actions/mappings.
    builder.addCase(projectSlice.actions.onLoadProject, resetDerivedState);
    builder.addCase(projectSlice.actions.onCreateProject, resetDerivedState);
  },
});

export const { setActionList, setAxisActionList, setInputBindingList, setCustomActionList, setFrameworkActionLists, setStartCallConfig } =
  editorSlice.actions;
export default editorSlice.reducer;
